import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { encodeValueForAnswerType, getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE } from 'components/Assessments/questionnaire'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { PatAssessmentFormField } from 'components/Assessments/PatAssessment/types/formField'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import {
  normalizeEquipmentCode,
  normalizeTherapyCode
} from 'components/Assessments/PatAssessment/helpers'

/**
 * Base adapter for handling Coding fields, yes/no questions, and notes fields
 * Provides common extraction and mutation building methods
 */
export abstract class CodingFieldAdapter extends BaseSubsectionAdapter<PatFormData> {
  /**
   * Type guard for Coding objects
   * Checks if value is an object with a code property
   */
  protected isCodingObject(value: unknown): value is { code: string; display?: string } {
    if (value === null || value === undefined || typeof value !== 'object') {
      return false
    }

    // Check if 'code' property exists
    if (!('code' in value)) {
      return false
    }

    // Check if 'code' is a string
    const codeValue = (value as { code: unknown }).code
    return typeof codeValue === 'string'
  }

  /**
   * Extract yes/no question value from flatAnswers
   * Returns { code, display } object from valueCoding, or undefined if not found
   */
  protected extractYesNoQuestion(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem
  ): { code: string; display: string } | undefined {
    const questionRaw = getAnswerValuesFromItems(flatAnswersArray, linkId, true)?.at(0)
    if (this.isCodingObject(questionRaw)) {
      return {
        code: (questionRaw.code || '').toLowerCase(),
        display: questionRaw.display || questionRaw.code || ''
      }
    }
    return undefined
  }

  /**
   * Extract notes field value from flatAnswers
   * Returns string value or undefined if not found
   */
  protected extractNotesField(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem
  ): string | undefined {
    return getAnswerValuesFromItems(flatAnswersArray, linkId)?.at(0)
  }

  /**
   * Extract single Coding value from flatAnswers
   * Returns { code, display } object from valueCoding, or undefined if not found */
  protected extractSingleCodingValue(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem,
    normalizeCode = true
  ): { code: string; display: string } | undefined {
    const rawValue = getAnswerValuesFromItems(flatAnswersArray, linkId, true)?.at(0)
    if (this.isCodingObject(rawValue)) {
      const code = rawValue.code || ''
      const display = rawValue.display || rawValue.code || ''
      // Only return if both code and display are present
      if (code && display) {
        return {
          code: normalizeCode ? code.toLowerCase() : code,
          display: display
        }
      }
    }
    return undefined
  }

  /**
   * Extract Coding array from flatAnswers
   * Returns array of { code, display } objects from valueCoding array
   */
  protected extractCodingArray(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem,
    normalizeCode = true
  ): Array<{ code: string; display: string }> {
    const rawValues = getAnswerValuesFromItems(flatAnswersArray, linkId, true)
    return (
      rawValues
        ?.map((item) => {
          // item is already the valueCoding object { code, display }
          if (this.isCodingObject(item)) {
            const code = item.code || ''
            return {
              code: normalizeCode ? code.toLowerCase() : code,
              display: item.display || item.code || ''
            }
          }
          return undefined
        })
        .filter((item): item is { code: string; display: string } => item !== undefined) || []
    )
  }

  /**
   * Extract therapy array with proper normalization
   * Normalizes codes to match the format used in therapyOptions
   */
  protected extractTherapyArray(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem
  ): Array<{ code: string; display: string }> {
    const rawValues = getAnswerValuesFromItems(flatAnswersArray, linkId, true)
    return (
      rawValues
        ?.map((item) => {
          if (this.isCodingObject(item)) {
            const storedCode = item.code || ''
            const display = item.display || item.code || ''
            const normalizedCode = normalizeTherapyCode(storedCode, 'code')
            return {
              code: normalizedCode,
              display: display
            }
          }
          return undefined
        })
        .filter((item): item is { code: string; display: string } => item !== undefined) || []
    )
  }

  /**
   * Extract equipment array with proper normalization
   * Normalizes codes to match the format used in equipmentOptions (e.g., "adaptive_utensils")
   */
  protected extractEquipmentArray(
    flatAnswersArray: QuestionnaireResponseItem[],
    linkId: PatAssessmentQuestionnaireItem
  ): Array<{ code: string; display: string }> {
    const rawValues = getAnswerValuesFromItems(flatAnswersArray, linkId, true)
    return (
      rawValues
        ?.map((item) => {
          if (this.isCodingObject(item)) {
            const storedCode = item.code || ''
            const display = item.display || item.code || ''
            const normalizedCode = normalizeEquipmentCode(storedCode)
            return {
              code: normalizedCode,
              display: display
            }
          }
          return undefined
        })
        .filter((item): item is { code: string; display: string } => item !== undefined) || []
    )
  }

  /**
   * Build mutation for yes/no question field
   * Returns mutation with lowercase code and original case display, or null if field is empty
   */
  protected buildYesNoQuestionMutation(
    formField: PatAssessmentFormField,
    linkId: PatAssessmentQuestionnaireItem
  ): FlatAnswerMutation | null {
    if (!this.formDataFieldExists(formField)) {
      return null
    }

    const question = this.getFormDataField(formField) as
      | { code: string; display: string }
      | undefined

    if (!question?.code) {
      return {
        linkId,
        value: undefined
      }
    }

    return {
      linkId,
      value: {
        code: question.code.toLowerCase(),
        display: question.display || question.code
      }
    }
  }

  /**
   * Build mutation for notes field
   * Returns mutation with value (or undefined to clear existing notes)
   */
  protected buildNotesFieldMutation(
    formField: PatAssessmentFormField,
    linkId: PatAssessmentQuestionnaireItem
  ): FlatAnswerMutation | null {
    if (!this.formDataFieldExists(formField)) {
      return null
    }

    const notes = this.getFormDataField(formField)
    const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[linkId]

    if (!answerType) {
      return null
    }

    // If notes encode to undefined (empty/cleared), return mutation with undefined to clear existing value
    if (encodeValueForAnswerType(notes, answerType) === undefined) {
      return {
        linkId,
        value: undefined
      }
    }

    return {
      linkId,
      value: notes
    }
  }

  /**
   * Build mutation for single Coding value field
   * Converts form data { code, display } object to Coding value
   * Returns mutation only if both code and display are non-empty
   */
  protected buildSingleCodingValueMutation(
    formField: PatAssessmentFormField,
    linkId: PatAssessmentQuestionnaireItem,
    normalizeCode = true
  ): FlatAnswerMutation | null {
    if (!this.formDataFieldExists(formField)) {
      return null
    }

    const formDataValue = this.getFormDataField(formField)
    // Form stores { code, display } object - direct conversion
    if (this.isCodingObject(formDataValue) && 'display' in formDataValue) {
      const rawCode = String(formDataValue.code || '')
      const codingValue: { code: string; display: string } = {
        code: normalizeCode ? rawCode.toLowerCase() : rawCode,
        display: String(formDataValue.display || '')
      }
      // Only push if both code and display are non-empty
      if (codingValue.code && codingValue.display) {
        return {
          linkId,
          value: codingValue
        }
      }
    }
    return {
      linkId,
      value: undefined
    }
  }

  /**
   * Build mutation for Coding array field
   * Converts form data array of { code, display } objects to Coding array
   * Returns mutation only if array has valid items
   */
  protected buildCodingArrayMutation(
    formField: PatAssessmentFormField,
    linkId: PatAssessmentQuestionnaireItem,
    normalizeCode = true
  ): FlatAnswerMutation | null {
    if (!this.formDataFieldExists(formField)) {
      return null
    }

    const formDataArray = this.getFormDataField(formField)
    // Form stores array of { code, display } objects - direct conversion
    if (!formDataArray || !Array.isArray(formDataArray)) {
      return null
    }

    if (formDataArray.length === 0) {
      return {
        linkId,
        value: undefined
      }
    }

    const codingArray = formDataArray
      .map((item) => {
        const rawCode = String(item.code || '')
        return {
          code: normalizeCode ? rawCode.toLowerCase() : rawCode,
          display: String(item.display || '')
        }
      })
      .filter(
        (item): item is { code: string; display: string } => item.code !== '' && item.display !== ''
      )

    if (codingArray.length === 0) {
      return {
        linkId,
        value: undefined
      }
    }

    return {
      linkId,
      value: codingArray
    }
  }
}
